// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon, Input, toast, Tooltip } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { CriteriaKeys } from 'components/CSAT/types'
import { usePutPaymentCaseMutation } from 'hooks/mutations/usePutPaymentCaseMutation'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { PaymentCaseUpdate } from 'types/payments'
import useCSATSurvey from '../hooks/useCSATSurvey'

interface CustomerIdInputProps {
  customerId?: string
  caseId: string
}

export const CustomerIdInput = ({ customerId, caseId }: CustomerIdInputProps) => {
  const { enableCsatSurveyCasePortal, getIsCriteriaCompleted, completeCSATCriteria } =
    useCSATSurvey()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const permissions = useMappedRoles().paymentSupport
  const canEdit = permissions?.canEdit
  const [customerIdInput, setCustomerIdInput] = useState('')
  const { mutate, isPending } = usePutPaymentCaseMutation()

  const hasEnteredCustomeridCriteriaCompleted = getIsCriteriaCompleted(
    CriteriaKeys.HAS_ENTERED_CUSTOMER_ID
  )

  const isEditingCustomerId = customerId === customerIdInput

  useEffect(() => {
    setCustomerIdInput(customerId ?? '')
  }, [customerId])

  const handleOnClick = async () => {
    const customerIdPayload: PaymentCaseUpdate = {
      customer: { customerId: customerIdInput },
      caseId,
    }

    mutate(customerIdPayload, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: reactQueryKeys.paymentSupportCases() })
        toast({ header: t('paymentSupport.successCustomerIdMessage'), variant: 'success' })

        !hasEnteredCustomeridCriteriaCompleted &&
          enableCsatSurveyCasePortal &&
          handleCSATSurveyUpdate()
      },
      onError: error => {
        setCustomerIdInput(customerId ?? '')
        toast({ header: t('paymentSupport.errorCustomerIdMessage'), variant: 'error' })
        logSentryError('Error: could not update payment case customer ID', error)
      },
    })
  }

  const handleCSATSurveyUpdate = () => {
    completeCSATCriteria(CriteriaKeys.HAS_ENTERED_CUSTOMER_ID)
  }

  return (
    <Box width="full" paddingY={12} flexCol>
      <Input
        testId="customer-id"
        label={t('paymentSupport.customerId')}
        inputMode="text"
        placeholderText={t('paymentSupport.customerIdPlaceholder')}
        name="customerId"
        value={customerIdInput}
        onChange={e => setCustomerIdInput(e.target.value)}
        disabled={!canEdit || isPending}
        Tooltip={<Tooltip bodyContent={t('paymentSupport.customerIdToolTip')} type={'complex'} />}
        IconButton={
          <Button
            loading={isPending}
            iconElement={
              <Tooltip
                position="top"
                bodyContent={t(
                  isEditingCustomerId
                    ? 'paymentSupport.customerIdEdit'
                    : 'paymentSupport.customerIdSave'
                )}
                type={'simple'}
              >
                <Box className="cursor-pointer">
                  <Icon
                    name={isEditingCustomerId ? 'SimplePencilCustom' : 'FloppyDisk'}
                    size="small"
                  />
                </Box>
              </Tooltip>
            }
            onClick={handleOnClick}
            disabled={!canEdit || isPending || !customerIdInput?.trim() || isEditingCustomerId}
            variant="ghost"
          />
        }
        iconPosition="right"
      />
    </Box>
  )
}
